/**
 * Punch-list f8 — Study Detail: full editable onboarding data, core
 * fields locked, full audit log on every edit.
 *
 * `protocol_number` / `title` / `sponsor` are deliberately NOT editable
 * here — those are the "core fields locked" the client asked for. Every
 * other onboarding-collected field (phase, therapeutic area, planned
 * dates, enrollment targets, sponsor study ID) already had backend
 * support via the onboarding wizard's Step2Payload but no post-onboarding
 * edit surface anywhere — this panel is that surface, backed by the new
 * PATCH /studies/{id} endpoint.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Pencil, Printer, Save, X } from 'lucide-react';
import { PrintHeader } from '@/components/print/PrintHeader';
import { useAuth } from '@/contexts/AuthContext';
import {
  binder,
  isBlindedStudyType,
  listActiveBinderTemplates,
  studies,
  type StudyRow,
  type StudySiteRow,
  type StudyBlindingType,
} from '@/api/client';
import { useHasRole } from '@/components/RoleGate';
import { errorText } from '@/lib/permissionError';
import { studyLabel } from '@/lib/studyLabel';

const WRITE_ROLES = [
  'site_study_designer',
  'regulatory_specialist',
  'principal_investigator',
  'sub_investigator',
  'coordinator',
] as const;

const PHASE_OPTIONS = ['', 'Phase 1', 'Phase 2', 'Phase 3', 'Phase 4', 'Observational'];

/** Punch-list DT2-BLIND-1. */
const BLINDING_TYPE_LABELS: Record<StudyBlindingType, string> = {
  open_label: 'Open-label',
  single_blind: 'Single-blind',
  double_blind: 'Double-blind',
  triple_blind: 'Triple-blind',
};

interface Draft {
  phase: string;
  therapeutic_area: string;
  planned_start_date: string;
  planned_end_date: string;
  enrollment_target: number;
  enrollment_target_site: number;
  sponsor_study_id: string;
  blinding_type: '' | StudyBlindingType;
  consent_mode: '' | 'paper' | 'electronic';
  binder_template_id: string;
  unblinded_binder_template_id: string;
}

function draftFrom(study: StudyRow, studySite: StudySiteRow): Draft {
  return {
    phase: study.phase ?? '',
    therapeutic_area: study.therapeutic_area ?? '',
    planned_start_date: (study.planned_start_date ?? '').split('T')[0] ?? '',
    planned_end_date: (study.planned_end_date ?? '').split('T')[0] ?? '',
    enrollment_target: study.enrollment_target,
    enrollment_target_site: studySite.enrollment_target_site ?? study.enrollment_target,
    sponsor_study_id: study.sponsor_study_id ?? '',
    blinding_type: study.blinding_type ?? '',
    consent_mode: study.consent_mode ?? '',
    binder_template_id: study.binder_template_id ?? '',
    unblinded_binder_template_id: study.unblinded_binder_template_id ?? '',
  };
}

export function StudyDetailsPanel({
  studyId,
  study,
  studySite,
  readOnly = false,
  editable,
}: {
  studyId: string;
  study: StudyRow;
  studySite: StudySiteRow;
  readOnly?: boolean;
  /**
   * Overrides the role check — the onboarding wizard passes true: every
   * onboarder edits the study there until activation (Harish, 2 Oct 2026).
   */
  editable?: boolean;
}) {
  const roleCanWrite = useHasRole(WRITE_ROLES);
  const canWrite = (editable ?? roleCanWrite) && !readOnly;
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(study, studySite));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const bindersQ = useQuery({
    queryKey: ['binder', 'list'],
    queryFn: binder.list,
    staleTime: 60_000,
  });
  const hasBinders = (bindersQ.data?.binders ?? []).some((b) => b.study_id === studyId);

  const templatesQ = useQuery({
    queryKey: ['binderTemplates', 'active'],
    queryFn: () => listActiveBinderTemplates(),
    staleTime: 5 * 60_000,
  });
  const templateName = (id: string | null | undefined) =>
    id ? (templatesQ.data?.templates.find((t) => t.template_id === id)?.name ?? id) : '—';
  const showUnblindedTemplate = isBlindedStudyType(editing ? (draft.blinding_type || null) : study.blinding_type);
  const blindedTemplates = (templatesQ.data?.templates ?? []).filter((t) => t.blinding_scope === 'blinded');
  const unblindedTemplates = (templatesQ.data?.templates ?? []).filter((t) => t.blinding_scope === 'unblinded');
  // Binder templates can change until the study's binder exists (at activation).
  const templatesEditable = editing && !hasBinders;

  // Re-sync the draft whenever the underlying data refetches, but only
  // while not actively editing — otherwise a background refetch would
  // clobber in-progress keystrokes.
  useEffect(() => {
    if (!editing) setDraft(draftFrom(study, studySite));
  }, [study, studySite, editing]);

  const save = useMutation({
    mutationFn: () =>
      studies.update(studyId, {
        phase: draft.phase || null,
        therapeutic_area: draft.therapeutic_area.trim() || null,
        planned_start_date: draft.planned_start_date || null,
        planned_end_date: draft.planned_end_date || null,
        enrollment_target: draft.enrollment_target,
        enrollment_target_site: draft.enrollment_target_site,
        sponsor_study_id: draft.sponsor_study_id.trim() || null,
        ...(hasBinders ? {} : { blinding_type: draft.blinding_type || null }),
        consent_mode: draft.consent_mode || null,
        ...(hasBinders ? {} : templatePatch()),
      }),
    onSuccess: () => {
      toast.success('Study details updated');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['workspace', studyId] });
      qc.invalidateQueries({ queryKey: ['onboarding', studyId] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to save')),
  });

  /** Only the template columns that changed (the server re-validates them). */
  function templatePatch() {
    const patch: { binder_template_id?: string | null; unblinded_binder_template_id?: string | null } = {};
    if (draft.binder_template_id !== (study.binder_template_id ?? '')) {
      patch.binder_template_id = draft.binder_template_id || null;
    }
    const unblinded = isBlindedStudyType(draft.blinding_type || null) ? draft.unblinded_binder_template_id : '';
    if (unblinded !== (study.unblinded_binder_template_id ?? '')) {
      patch.unblinded_binder_template_id = unblinded || null;
    }
    return patch;
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!hasBinders && !draft.binder_template_id) {
      errs.binder_template_id = 'Choose a binder template.';
    }
    if (!hasBinders && isBlindedStudyType(draft.blinding_type || null) && !draft.unblinded_binder_template_id) {
      errs.unblinded_binder_template_id = 'A blinded study needs an unblinded binder template.';
    }
    if (
      draft.planned_start_date &&
      draft.planned_end_date &&
      draft.planned_end_date < draft.planned_start_date
    ) {
      errs.planned_end_date = 'End date must be after start date.';
    }
    if (draft.enrollment_target < 0) errs.enrollment_target = 'Must be 0 or greater.';
    if (
      draft.enrollment_target > 0 &&
      draft.enrollment_target_site > draft.enrollment_target
    ) {
      errs.enrollment_target_site = 'Site target cannot exceed the global target.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  return (
    <div className="card" data-print-root>
      <PrintHeader
        title="Study details"
        subtitle={studyLabel(study)}
        printedBy={user?.fullName ?? user?.email}
      />
      <div
        data-print-hide
        className="flex items-center justify-between border-b border-surface-border bg-surface-muted/50 px-4 py-3"
      >
        <h2 className="text-sm font-semibold text-ink-primary">Study details</h2>
        {!editing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => window.print()}
              title="Print this page with the DreamTrials header"
            >
              <Printer className="h-3.5 w-3.5" /> Print
            </button>
            {canWrite && (
              <button
                type="button"
                className="btn-secondary text-xs"
                onClick={() => setEditing(true)}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setEditing(false);
                setDraft(draftFrom(study, studySite));
                setErrors({});
              }}
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={save.isPending}
              onClick={() => validate() && save.mutate()}
            >
              {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-4 p-4 md:grid-cols-2">
        <div>
          <label className="label text-xs">Protocol number</label>
          <input className="input bg-surface-muted text-ink-secondary cursor-default" value={study.protocol_number} readOnly title="Locked — set at study creation" />
        </div>
        <div>
          <label className="label text-xs">Title</label>
          <input className="input bg-surface-muted text-ink-secondary cursor-default" value={study.title} readOnly title="Locked — set at study creation" />
        </div>
        <div>
          <label className="label text-xs">Sponsor</label>
          <input className="input bg-surface-muted text-ink-secondary cursor-default" value={study.sponsor} readOnly title="Locked — set at study creation" />
        </div>

        <div>
          <label className="label text-xs">Sponsor study ID</label>
          {editing ? (
            <input
              className="input"
              maxLength={120}
              value={draft.sponsor_study_id}
              onChange={(e) => setDraft({ ...draft, sponsor_study_id: e.target.value })}
              placeholder="Sponsor's own identifier for this study"
            />
          ) : (
            <input className="input bg-surface-muted text-ink-secondary cursor-default" value={draft.sponsor_study_id || '—'} readOnly />
          )}
        </div>
        <div>
          <label className="label text-xs">Phase</label>
          {editing ? (
            <select className="input" value={draft.phase} onChange={(e) => setDraft({ ...draft, phase: e.target.value })}>
              {PHASE_OPTIONS.map((p) => <option key={p} value={p}>{p || '— none —'}</option>)}
            </select>
          ) : (
            <input className="input bg-surface-muted text-ink-secondary cursor-default" value={draft.phase || '—'} readOnly />
          )}
        </div>
        <div>
          <label className="label text-xs">Blinding design</label>
          {editing ? (
            <select
              className="input"
              value={draft.blinding_type}
              disabled={hasBinders}
              title={hasBinders ? 'Locked — binders already exist for this study' : undefined}
              onChange={(e) => setDraft({ ...draft, blinding_type: e.target.value as '' | StudyBlindingType })}
            >
              <option value="">— none —</option>
              {(Object.keys(BLINDING_TYPE_LABELS) as StudyBlindingType[]).map((t) => (
                <option key={t} value={t}>{BLINDING_TYPE_LABELS[t]}</option>
              ))}
            </select>
          ) : (
            <input
              className="input bg-surface-muted text-ink-secondary cursor-default"
              value={draft.blinding_type ? BLINDING_TYPE_LABELS[draft.blinding_type] : '—'}
              readOnly
            />
          )}
        </div>

        {/* Feedback (2026-09-29) — a paper-only study is exempt from the
            signature-layout requirement at activation. Electronic is the
            default for every existing study. */}
        <div>
          <label className="label text-xs">Consent mode</label>
          {editing ? (
            <select
              className="input"
              value={draft.consent_mode}
              onChange={(e) =>
                setDraft({ ...draft, consent_mode: e.target.value as '' | 'paper' | 'electronic' })
              }
            >
              <option value="">Electronic (default)</option>
              <option value="electronic">Electronic</option>
              <option value="paper">Paper only</option>
            </select>
          ) : (
            <input
              className="input bg-surface-muted text-ink-secondary cursor-default"
              value={draft.consent_mode === 'paper' ? 'Paper only' : 'Electronic'}
              readOnly
            />
          )}
          <p className="mt-1 text-[11px] text-ink-muted">
            Paper-only studies activate without a signature layout placed on the ICF.
          </p>
        </div>

        <div>
          <label className="label text-xs">
            {showUnblindedTemplate ? 'Blinded binder template' : 'Study binder template'}
          </label>
          {templatesEditable ? (
            <select
              className={`input ${errors.binder_template_id ? 'border-danger-500' : ''}`}
              value={draft.binder_template_id}
              onChange={(e) => setDraft({ ...draft, binder_template_id: e.target.value })}
            >
              <option value="">— Select template —</option>
              {blindedTemplates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.is_default === 'true' ? 'Default · ' : ''}{t.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="input bg-surface-muted text-ink-secondary cursor-default"
              value={templateName(editing ? draft.binder_template_id : study.binder_template_id)}
              readOnly
              title={hasBinders ? 'Locked — the study binder already exists' : undefined}
            />
          )}
          {errors.binder_template_id && <p className="mt-1 text-xs text-danger-500">{errors.binder_template_id}</p>}
        </div>
        {showUnblindedTemplate && (
          <div>
            <label className="label text-xs">Unblinded binder template</label>
            {templatesEditable ? (
              <select
                className={`input ${errors.unblinded_binder_template_id ? 'border-danger-500' : ''}`}
                value={draft.unblinded_binder_template_id}
                onChange={(e) => setDraft({ ...draft, unblinded_binder_template_id: e.target.value })}
              >
                <option value="">— Select template —</option>
                {unblindedTemplates.map((t) => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.is_default === 'true' ? 'Default · ' : ''}{t.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="input bg-surface-muted text-ink-secondary cursor-default"
                value={templateName(editing ? draft.unblinded_binder_template_id : study.unblinded_binder_template_id)}
                readOnly
                title={hasBinders ? 'Locked — the study binder already exists' : undefined}
              />
            )}
            {errors.unblinded_binder_template_id && (
              <p className="mt-1 text-xs text-danger-500">{errors.unblinded_binder_template_id}</p>
            )}
          </div>
        )}

        <div className="md:col-span-2">
          <label className="label text-xs">Therapeutic area</label>
          {editing ? (
            <input
              className="input"
              maxLength={120}
              value={draft.therapeutic_area}
              onChange={(e) => setDraft({ ...draft, therapeutic_area: e.target.value })}
            />
          ) : (
            <input className="input bg-surface-muted text-ink-secondary cursor-default" value={draft.therapeutic_area || '—'} readOnly />
          )}
        </div>

        <div>
          <label className="label text-xs">Planned start</label>
          {editing ? (
            <input
              type="date"
              className="input"
              value={draft.planned_start_date}
              onChange={(e) => setDraft({ ...draft, planned_start_date: e.target.value })}
            />
          ) : (
            <input className="input bg-surface-muted text-ink-secondary cursor-default" value={draft.planned_start_date || '—'} readOnly />
          )}
        </div>
        <div>
          <label className="label text-xs">Planned end</label>
          {editing ? (
            <input
              type="date"
              className={`input ${errors.planned_end_date ? 'border-danger-500' : ''}`}
              value={draft.planned_end_date}
              onChange={(e) => setDraft({ ...draft, planned_end_date: e.target.value })}
            />
          ) : (
            <input className="input bg-surface-muted text-ink-secondary cursor-default" value={draft.planned_end_date || '—'} readOnly />
          )}
          {errors.planned_end_date && <p className="mt-1 text-xs text-danger-500">{errors.planned_end_date}</p>}
        </div>

        <div>
          <label className="label text-xs">Enrollment target (global)</label>
          {editing ? (
            <input
              type="number"
              min={0}
              className={`input ${errors.enrollment_target ? 'border-danger-500' : ''}`}
              value={draft.enrollment_target}
              onChange={(e) => setDraft({ ...draft, enrollment_target: parseInt(e.target.value || '0', 10) })}
            />
          ) : (
            <input className="input bg-surface-muted text-ink-secondary cursor-default" value={draft.enrollment_target} readOnly />
          )}
          {errors.enrollment_target && <p className="mt-1 text-xs text-danger-500">{errors.enrollment_target}</p>}
        </div>
        <div>
          <label className="label text-xs">Enrollment target (this site)</label>
          {editing ? (
            <input
              type="number"
              min={0}
              className={`input ${errors.enrollment_target_site ? 'border-danger-500' : ''}`}
              value={draft.enrollment_target_site}
              onChange={(e) => setDraft({ ...draft, enrollment_target_site: parseInt(e.target.value || '0', 10) })}
            />
          ) : (
            <input className="input bg-surface-muted text-ink-secondary cursor-default" value={draft.enrollment_target_site} readOnly />
          )}
          {errors.enrollment_target_site && <p className="mt-1 text-xs text-danger-500">{errors.enrollment_target_site}</p>}
        </div>
      </div>
    </div>
  );
}
